import { Component, OnInit, ViewChild, AfterViewInit, OnDestroy, ElementRef } from '@angular/core'
import { RouterLinkActive, RouterLink, RouterOutlet } from '@angular/router'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { TranslateModule } from '@ngx-translate/core'
import { VersionService } from '../services/version.service'
import { AsyncPipe } from '@angular/common'
import { BehaviorSubject, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { ScrollToTopComponent } from '../helpers/components/scroll-to-top/scroll-to-top.component'
import { environment } from '../../environments/environment'
import { ExportToastComponent } from '../features/export/export-toast/export-toast.component'
import { AuthService } from '../services/auth/auth.service'

interface SidebarSection {
    name: string
    icon: string
    route: string
    /** Nascosta a chi non ha `superAmministratore: true` sull'utente loggato. */
    adminOnly?: boolean
}

@Component({
    selector: 'app-left-sidebar',
    templateUrl: './left-sidebar.component.html',
    styleUrls: ['./left-sidebar.component.scss'],
    imports: [
        ToolbarComponent,
        RouterLinkActive,
        RouterLink,
        RouterOutlet,
        TranslateModule,
        ScrollToTopComponent,
        AsyncPipe,
        ExportToastComponent,
    ],
})
export class LeftSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
    version = ''
    isCollapsed = false
    isScrollTopVisible$ = new BehaviorSubject<boolean>(false)
    private destroy$ = new Subject<void>()

    @ViewChild('mainContent') mainContentRef!: ElementRef<HTMLElement>

    constructor(
        private _versionService: VersionService,
        private _authService: AuthService
    ) {}

    ngOnInit() {
        this._versionService
            .getAppVersion()
            .pipe(takeUntil(this.destroy$))
            .subscribe((data) => {
                this.version = data.version
            })
    }

    ngAfterViewInit() {
        this.mainContentRef.nativeElement.addEventListener('scroll', () => {
            this.isScrollTopVisible$.next(this.mainContentRef.nativeElement.scrollTop > 200)
        })
    }

    handleScrollToTop(): void {
        this.mainContentRef.nativeElement.scrollTo({ top: 0, behavior: 'smooth' })
    }

    ngOnDestroy() {
        this.destroy$.next()
        this.destroy$.complete()
    }

    onToggleSidenav(): void {
        this.isCollapsed = !this.isCollapsed
    }

    onBlankPageClick(): void {
        window.open('https://test-inventario-demo.muacloud.it/P/', '_blank')
    }

    sections: SidebarSection[] = [
        { name: 'DASHBOARD', icon: 'dashboard', route: '/dashboard' },
        { name: 'DEVICES', icon: 'devices', route: '/devices' },
        { name: 'SERVICES', icon: 'miscellaneous_services', route: '/services' },
        { name: 'BACKUPS', icon: 'backup', route: '/backups' },
        { name: 'SOFTWARE', icon: 'code', route: '/software' },
        {
            name: 'INSTALLAZIONI_SOFTWARE.MENU_TITLE',
            icon: 'install_desktop',
            route: '/installazioni-software',
        },
        { name: 'ASSOCIATIONS.MENU_TITLE', icon: 'link', route: '/associations' },
        { name: 'ANAGRAFICA.MENU_TITLE', icon: 'business', route: '/anagrafica' },
        //{ name: 'VULNERABILITY', icon: 'security', route: '/vulnerability' },
        { name: 'IMPORT_EXPORT.MENU_TITLE', icon: 'import_export', route: '/import' },
        //{ name: 'REPORTS', icon: 'description', route: '/report' },
        //{ name: 'SCAN_RESULTS', icon: 'assessment', route: '/scan-results' },
        //{ name: 'SCHEDULE_SCAN', icon: 'schedule', route: '/schedule-scan' },
        //{ name: 'START_SYNC_WITH_MUA', icon: 'sync', route: '/start-sync-mua' },
        {
            name: 'ATTRIBUTES_ADMIN.MENU_TITLE',
            icon: 'tune',
            route: '/admin/attributi-globali',
            adminOnly: true,
        },
        { name: 'ENTI_ADMIN_MENU', icon: 'domain_add', route: '/admin/enti', adminOnly: true },
    ]

    /** `sections` filtrata: le voci `adminOnly` compaiono solo per chi ha `superAmministratore: true`. */
    visibleSections(): SidebarSection[] {
        const isSuperAdmin = this._authService.getUserResult()?.superAmministratore === true
        return this.sections.filter((s) => !s.adminOnly || isSuperAdmin)
    }
}
